import { useState, type FormEvent } from "react"
import { QRCodeSVG } from "qrcode.react"
import { Copy, Check, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog } from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { Badge } from "@/components/ui/page"
import { FormAlert } from "@/components/auth/FormAlert"
import { authService } from "@/services/authService"
import { useAuthStore } from "@/store/authStore"

type Step = "idle" | "setup" | "confirm" | "disable"

interface TwoFactorSetupModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function TwoFactorSetupModal({ open, onClose, onSuccess }: TwoFactorSetupModalProps) {
  const user = useAuthStore((s) => s.user)
  const [step, setStep] = useState<Step>("idle")
  const [secret, setSecret] = useState("")
  const [otpAuthUri, setOtpAuthUri] = useState("")
  const [code, setCode] = useState("")
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const reset = () => { setStep("idle"); setCode(""); setError("") }
  const close = () => { reset(); onClose() }

  const startSetup = async () => {
    setLoading(true); setError("")
    try {
      const res = await authService.setup2fa()
      setSecret(res.secret)
      setOtpAuthUri(res.otpAuthUri)
      setStep("setup")
    } catch {
      setError("Não foi possível iniciar a configuração. Tente de novo.")
    } finally {
      setLoading(false)
    }
  }

  const submitCode = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true); setError("")
    try {
      if (step === "confirm") await authService.enable2fa(code)
      else await authService.disable2fa(code)
      onSuccess()
      close()
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || "Código inválido. Confira o horário do celular e tente de novo.")
    } finally {
      setLoading(false)
    }
  }

  const copySecret = () => {
    navigator.clipboard.writeText(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const codeStep = step === "confirm" || step === "disable"
  const enabled = !!user?.twoFactorEnabled

  return (
    <Dialog
      open={open}
      size="sm"
      onClose={close}
      busy={loading}
      title="Verificação em duas etapas"
      description={step === "setup" ? "Passo 1 de 2 · cadastre no aplicativo" : step === "confirm" ? "Passo 2 de 2 · confirme o código" : undefined}
      onSubmit={codeStep ? submitCode : undefined}
      footer={
        step === "idle" ? (
          enabled ? (
            <>
              <Button type="button" variant="outline" onClick={close}>Fechar</Button>
              <Button type="button" variant="destructive" onClick={() => { setStep("disable"); setCode("") }}>Desativar</Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={close}>Agora não</Button>
              <Button type="button" onClick={startSetup} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin" />} Começar</Button>
            </>
          )
        ) : step === "setup" ? (
          <>
            <Button type="button" variant="outline" onClick={close}>Cancelar</Button>
            <Button type="button" onClick={() => { setStep("confirm"); setCode("") }}>Já cadastrei</Button>
          </>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={step === "confirm" ? () => setStep("setup") : reset}>Voltar</Button>
            <Button type="submit" variant={step === "disable" ? "destructive" : "default"} disabled={loading || code.length !== 6}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {step === "confirm" ? "Ativar" : "Desativar"}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error && <FormAlert>{error}</FormAlert>}

        {step === "idle" && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-[14px]">Situação: {enabled ? <Badge tone="positive">Ativa</Badge> : <Badge>Inativa</Badge>}</p>
            <p className="text-[14px] text-muted-foreground leading-relaxed">
              {enabled
                ? "Ao entrar, pedimos o código do seu aplicativo autenticador além da senha. Para desativar, você vai precisar de um código atual."
                : "Além da senha, o login passa a pedir um código de 6 dígitos que muda a cada 30 segundos no seu celular. Funciona com Google Authenticator, Microsoft Authenticator, Authy e 1Password."}
            </p>
          </div>
        )}

        {step === "setup" && (
          <div className="space-y-4">
            <p className="text-[14px] text-muted-foreground">No aplicativo autenticador, adicione uma conta e escaneie o código abaixo.</p>
            <div className="flex justify-center">
              <div className="rounded-lg border bg-white p-3"><QRCodeSVG value={otpAuthUri} size={168} /></div>
            </div>
            <div className="space-y-1.5">
              <p className="text-[13px] font-medium">Sem câmera? Digite esta chave</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[12px] bg-inset rounded-md px-2.5 py-2 font-mono break-all">{secret}</code>
                <Button type="button" variant="outline" size="icon" onClick={copySecret} aria-label={copied ? "Chave copiada" : "Copiar chave"}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {codeStep && (
          <Field label="Código do autenticador" htmlFor="twofa-code"
            hint={step === "confirm" ? "Digite o código que aparece agora no aplicativo." : "Digite um código atual para confirmar a desativação."}>
            <Input
              id="twofa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              // Filtra antes de limitar: autenticadores exibem "123 456" e o código colado tem 7 caracteres
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="h-12 text-center text-[22px] tracking-[0.5em] num"
              required
            />
          </Field>
        )}
      </div>
    </Dialog>
  )
}
